import { useRef } from "react";
import { Sparkles, Check } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";

gsap.registerPlugin(ScrollTrigger);

const HookSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subtextRef = useRef<HTMLParagraphElement>(null);
  const ctaGroupRef = useRef<HTMLDivElement>(null);
  const valueStackRef = useRef<HTMLDivElement>(null);
  const bottomNoteRef = useRef<HTMLParagraphElement>(null);

  // GSAP Animations
  useGSAP(() => {
    const ctx = gsap.context(() => {
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      
      if (prefersReducedMotion) {
        // Static render - no animations
        return;
      }

      const mm = gsap.matchMedia();

      // Desktop only animations
      mm.add("(min-width: 768px)", () => {
        // Badge: fade + scale
        if (badgeRef.current) {
          gsap.from(badgeRef.current, {
            opacity: 0,
            scale: 0.96,
            duration: 0.8,
            ease: "power3.out",
            force3D: true,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 80%",
              once: true,
            },
          });

          // Badge breathing animation
          gsap.to(badgeRef.current, {
            scale: 1.04,
            duration: 3.5,
            ease: "sine.inOut",
            repeat: -1,
            yoyo: true,
            force3D: true,
          });
        }

        // Headline: fade + y
        if (headlineRef.current) {
          gsap.from(headlineRef.current, {
            opacity: 0,
            y: 18,
            duration: 1,
            ease: "power3.out",
            force3D: true,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 80%",
              once: true,
            },
          });
        }

        // Subtext: fade in
        if (subtextRef.current) {
          gsap.from(subtextRef.current, {
            opacity: 0,
            duration: 0.8,
            delay: 0.2,
            ease: "power2.out",
            force3D: true,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 80%",
              once: true,
            },
          });
        }

        // Value stack bullets: stagger
        if (valueStackRef.current) {
          const bullets = valueStackRef.current.querySelectorAll(".value-bullet");
          gsap.from(bullets, {
            opacity: 0,
            y: 12,
            duration: 0.6,
            stagger: 0.1,
            ease: "power2.out",
            force3D: true,
            scrollTrigger: {
              trigger: valueStackRef.current,
              start: "top 85%",
              once: true,
            },
          });
        }

        // CTA group: fade in
        if (ctaGroupRef.current) {
          gsap.from(ctaGroupRef.current, {
            opacity: 0,
            duration: 0.8,
            delay: 0.3,
            ease: "power2.out",
            force3D: true,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 80%",
              once: true,
            },
          });
        }

        // Bottom note: fade in
        if (bottomNoteRef.current) {
          gsap.from(bottomNoteRef.current, {
            opacity: 0,
            duration: 0.6,
            delay: 0.5,
            ease: "power2.out",
            force3D: true,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 80%",
              once: true,
            },
          });
        }

      });

      // Mobile: static render
      mm.add("(max-width: 767px)", () => {
        // Ensure all elements are visible
        const elements = [
          badgeRef.current,
          headlineRef.current,
          subtextRef.current,
          ctaGroupRef.current,
          valueStackRef.current,
          bottomNoteRef.current,
        ].filter(Boolean);

        gsap.set(elements, { opacity: 1, y: 0, scale: 1 });
      });

      return () => mm.revert();
    }, sectionRef);

    return () => ctx.revert();
  }, { scope: sectionRef });

  const handlePrimaryCTA = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView();
    }
  };


  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-[100svh] flex items-center justify-center px-4 sm:px-6 pt-[calc(env(safe-area-inset-top)+4rem)] sm:pt-24 pb-24 md:pb-24 hook-section scroll-mt-20"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 hook-bg-pattern overflow-hidden" />
      <div className="absolute inset-0 hook-purple-glow overflow-hidden" />
      <div className="absolute inset-0 hook-grain-overlay overflow-hidden" />

      <div className="relative z-10 w-full max-w-7xl mx-auto py-8 md:py-12 overflow-visible">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          {/* Left Column - 7 cols on desktop */}
          <div className="lg:col-span-7 relative z-10 space-y-4 md:space-y-5">
            {/* Badge */}
            <div
              ref={badgeRef}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 backdrop-blur-sm mt-4 sm:mt-0"
              style={{ willChange: "transform, opacity" }}
            >
              <Sparkles className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium text-foreground/80">Újgenerációs Webfejlesztés</span>
            </div>

            {/* Headline */}
            <h1
              ref={headlineRef}
              className="font-heading text-[clamp(2.2rem,4.5vw,4.4rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-balance"
              style={{ willChange: "transform, opacity" }}
            >
              <span className="block">Exkluzív Webmegjelenés.</span>
              <span className="block mt-4">
                <span className="hook-outlined-text">Kompromisszumok</span> Nélkül.
              </span>
            </h1>

            {/* Subtext */}
            <p
              ref={subtextRef}
              className="text-sm md:text-base lg:text-lg text-white/70 leading-[1.75] max-w-lg mt-4"
              style={{ willChange: "opacity" }}
            >
              Egyedi fejlesztésű, villámgyors weboldalak mindazoknak, akik kaput nyitnának az online növekedés felé. Teljes körű menedzsmenttel és dedikált szakértelemmel támogatlak, hogy a technológia ne akadály, hanem valódi versenyelőny legyen.
            </p>
          </div>

          {/* Right Column - 5 cols on desktop */}
          <div className="lg:col-span-5 relative z-10 space-y-4 md:space-y-5 w-full flex flex-col items-center lg:items-start text-center lg:text-left max-w-md lg:max-w-none mx-auto lg:mx-0">
            {/* Value Stack */}
            <div
              ref={valueStackRef}
              className="hook-value-stack p-5 md:p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 w-full max-w-md mx-auto lg:mx-0"
            >
              <div className="space-y-3">
                <div className="value-bullet flex items-center lg:items-start gap-3 justify-center lg:justify-start">
                  <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 lg:mt-0.5">
                    <Check className="w-3 h-3 text-accent" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Villámgyors betöltés</div>
                    <div className="text-sm text-muted-foreground">Optimalizált teljesítmény</div>
                  </div>
                </div>
                <div className="value-bullet flex items-center lg:items-start gap-3 justify-center lg:justify-start">
                  <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 lg:mt-0.5">
                    <Check className="w-3 h-3 text-accent" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Teljes karbantartás</div>
                    <div className="text-sm text-muted-foreground">Folyamatos támogatás</div>
                  </div>
                </div>
                <div className="value-bullet flex items-center lg:items-start gap-3 justify-center lg:justify-start">
                  <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 lg:mt-0.5">
                    <Check className="w-3 h-3 text-accent" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">AI optimalizált</div>
                    <div className="text-sm text-muted-foreground">Jövőálló struktúra</div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div ref={ctaGroupRef} className="space-y-3 flex flex-col items-center justify-center w-full lg:w-auto lg:items-start" style={{ willChange: "opacity" }}>
              <Button
                size="lg"
                onClick={handlePrimaryCTA}
                className="w-full max-w-sm sm:w-auto sm:max-w-xs bg-primary text-primary-foreground hover:bg-primary/90 text-base md:text-lg px-6 py-5 rounded-full font-medium"
              >
                Ingyenes Látványtervet Kérek
              </Button>
            </div>

            {/* Bottom Note */}
            <div className="border-t border-border/30 pt-4">
              <p
                ref={bottomNoteRef}
                className="text-xs md:text-sm text-muted-foreground/70 leading-relaxed text-center"
                style={{ willChange: "opacity" }}
              >
                A befektetés mértéke minden esetben egyedi, a céljaidhoz igazítva. Fix díjas csomagok helyett személyre szabott megoldásokat kínálok.
              </p>
            </div>
          </div>
        </div>

        {/* Scroll Hint - Hidden to save space */}
      </div>
    </section>
  );
};

export default HookSection;
